// Shared constants for the Accounting module pages.
// Ported as-is from the old monolithic Accounting section that used to live
// inside HousingSocietyPanel.jsx (Billing tab -> "Accounting" sub-menu), so
// existing ledger names / voucher types / opening-balance shapes stay
// compatible with data already saved through that old UI.

export const ledgerGroupLabels = {
  bank: "Bank accounts",
  cash: "Cash",
  income: "Income",
  expense: "Expenses",
  vendor: "Vendors / Payables",
  member: "Members",
  asset: "Assets",
  liability: "Liabilities",
  fund: "Funds",
  equity: "Capital / opening",
  other: "Other",
};

export const voucherTypeOptions = ["Receipt", "Payment", "Purchase", "Journal", "Contra", "Opening"];

export const defaultAccountingLedgers = [
  { id: "system-main-bank", name: "Main bank", ledgerGroup: "bank", openingSide: "debit", active: true, system: true },
  { id: "system-cash", name: "Cash", ledgerGroup: "cash", openingSide: "debit", active: true, system: true },
  { id: "system-member-receivables", name: "Member receivables", ledgerGroup: "asset", openingSide: "debit", active: true, system: true },
  { id: "system-fixed-deposits", name: "Fixed deposits", ledgerGroup: "asset", openingSide: "debit", active: true, system: true },
  { id: "system-maintenance-income", name: "Maintenance income", ledgerGroup: "income", openingSide: "credit", active: true, system: true },
  { id: "system-scrap-sale-income", name: "Scrap sale income", ledgerGroup: "income", openingSide: "credit", active: true, system: true },
  { id: "system-interest-income", name: "Interest income", ledgerGroup: "income", openingSide: "credit", active: true, system: true },
  { id: "system-security-salary", name: "Security salary expense", ledgerGroup: "expense", openingSide: "debit", active: true, system: true },
  { id: "system-housekeeping", name: "Housekeeping expense", ledgerGroup: "expense", openingSide: "debit", active: true, system: true },
  { id: "system-repairs", name: "Repairs and maintenance", ledgerGroup: "expense", openingSide: "debit", active: true, system: true },
  { id: "system-electricity", name: "Electricity expense", ledgerGroup: "expense", openingSide: "debit", active: true, system: true },
  { id: "system-vendor", name: "Vendor payable", ledgerGroup: "vendor", openingSide: "credit", active: true, system: true },
  { id: "system-watchman", name: "Watchman payable", ledgerGroup: "vendor", openingSide: "credit", active: true, system: true },
  { id: "system-sweeper", name: "Sweeper payable", ledgerGroup: "vendor", openingSide: "credit", active: true, system: true },
  { id: "system-reserve", name: "Reserve fund", ledgerGroup: "fund", openingSide: "credit", active: true, system: true },
];

// Note: openingBalance/openingSide are sent to the backend (which still
// requires them on SocietyEngineLedgerIn for Phase 3 compatibility) but are
// deliberately NOT exposed as editable fields on the Ledger create/edit form
// per the Phase 4 rule against embedding an opening-balance shortcut into
// ordinary ledger master data - real opening balances belong to a later
// migration/go-live phase, not routine ledger creation.
export const ledgerBlank = {
  name: "",
  ledgerGroup: "expense",
  accountGroupId: "",
  code: "",
  openingBalance: 0,
  openingSide: "debit",
  openingAsOfDate: "",
  active: true,
  notes: "",
};

export const accountingEntryBlank = {
  entryDate: new Date().toISOString().slice(0, 10),
  entryType: "expense",
  voucherType: "Payment",
  account: "Main bank",
  category: "",
  debitLedger: "Main bank",
  creditLedger: "",
  amount: "",
  mode: "bank_transfer",
  partyName: "",
  referenceNo: "",
  description: "",
  attachmentUrl: "",
};

export const accountingQuickActions = [
  { label: "Maintenance received", voucherType: "Receipt", entryType: "income", debitLedger: "Main bank", creditLedger: "Maintenance income", ledgerGroup: "income", account: "Main bank", category: "Maintenance income", mode: "upi", partyName: "Member", description: "Maintenance payment received" },
  { label: "Purchase / vendor bill", voucherType: "Purchase", entryType: "expense", debitLedger: "Repairs and maintenance", creditLedger: "Vendor payable", ledgerGroup: "expense", account: "Repairs and maintenance", category: "Vendor payable", mode: "bank_transfer", partyName: "Vendor", description: "Vendor bill booked" },
  { label: "Vendor payment", voucherType: "Payment", entryType: "expense", debitLedger: "Vendor payable", creditLedger: "Main bank", ledgerGroup: "vendor", account: "Vendor payable", category: "Main bank", mode: "bank_transfer", partyName: "Vendor", description: "Vendor payment made" },
  { label: "Watchman salary", voucherType: "Payment", entryType: "expense", debitLedger: "Security salary expense", creditLedger: "Main bank", ledgerGroup: "expense", account: "Security salary expense", category: "Main bank", mode: "bank_transfer", partyName: "Watchman", description: "Watchman salary paid" },
  { label: "Sweeper salary", voucherType: "Payment", entryType: "expense", debitLedger: "Housekeeping expense", creditLedger: "Main bank", ledgerGroup: "expense", account: "Housekeeping expense", category: "Main bank", mode: "bank_transfer", partyName: "Sweeper", description: "Housekeeping salary paid" },
  { label: "Scrap sold", voucherType: "Receipt", entryType: "income", debitLedger: "Cash", creditLedger: "Scrap sale income", ledgerGroup: "income", account: "Cash", category: "Scrap sale income", mode: "cash", partyName: "Scrap buyer", description: "Scrap sale receipt" },
  { label: "Bank deposit", voucherType: "Contra", entryType: "bank_deposit", debitLedger: "Main bank", creditLedger: "Cash", ledgerGroup: "bank", account: "Main bank", category: "Cash", mode: "bank_transfer", partyName: "Bank", description: "Cash deposited into bank" },
  { label: "FD opened", voucherType: "Contra", entryType: "fd_open", debitLedger: "Fixed deposits", creditLedger: "Main bank", ledgerGroup: "asset", account: "Fixed deposits", category: "Main bank", mode: "bank_transfer", partyName: "Bank", description: "Fixed deposit opened" },
  { label: "FD interest", voucherType: "Receipt", entryType: "fd_interest", debitLedger: "Main bank", creditLedger: "Interest income", ledgerGroup: "income", account: "Main bank", category: "Interest income", mode: "bank_transfer", partyName: "Bank", description: "Fixed deposit interest received" },
];

export const openingBalanceBlank = [
  { balanceType: "bank", label: "Opening bank balance", account: "Main bank", amount: "", referenceNo: "", notes: "" },
  { balanceType: "cash", label: "Opening cash balance", account: "Cash", amount: "", referenceNo: "", notes: "" },
  { balanceType: "fd", label: "Opening fixed deposit", account: "Fixed deposits", amount: "", referenceNo: "", notes: "" },
  { balanceType: "investment", label: "Opening investment", account: "Investments", amount: "", referenceNo: "", notes: "" },
  { balanceType: "receivable", label: "Opening member outstanding", account: "Member receivables", amount: "", referenceNo: "", notes: "" },
  { balanceType: "payable", label: "Opening payable", account: "Payables", amount: "", referenceNo: "", notes: "" },
  { balanceType: "fund", label: "Opening reserve fund", account: "Reserve fund", amount: "", referenceNo: "", notes: "" },
];

export const accountingSetupBlank = {
  financialYear: "",
  goLiveDate: new Date().toISOString().slice(0, 10),
  migrationMode: "opening_balance",
  status: "draft",
  notes: "",
};

export const migrationBlank = {
  financialYear: "",
  goLiveDate: new Date().toISOString().slice(0, 10),
  migrationMode: "opening_balance",
  openingTrialBalance: [{ account: "", group: "", debit: "", credit: "", remarks: "" }],
  memberBalances: [{ ownerId: "", wing: "", flatNumber: "", ownerName: "", outstanding: "", advance: "", billReference: "", dueDate: "", remarks: "" }],
  bankBalances: [{ account: "Main bank", bankName: "", accountNumber: "", amount: "", unreconciledAmount: "", remarks: "" }],
  cashBalances: [],
  fdBalances: [],
  fundBalances: [],
  vendorBalances: [],
  assetBalances: [],
  notes: "",
};

export const bankAccountTypeLabels = {
  savings: "Savings",
  current: "Current",
  od: "Overdraft (OD)",
  cc: "Cash Credit (CC)",
  other: "Other",
};

// Sentinel value for "create a brand new ledger for this bank" instead of
// linking to an existing one. Defaulting to this (rather than an existing
// ledger id) avoids every new bank account silently reusing the generic
// system "Main bank" ledger, which left every voucher screen showing "Main
// bank" no matter what real bank name the user entered.
export const NEW_LEDGER_OPTION = "__new__";

// Accounting Bank Account master - deliberately separate from the Billing
// "Primary Settlement Account" (society/billing/settings). See the backend
// SocietyBankAccountIn model for the full rationale.
//
// Phase 5 correction: the normal Add/Edit Bank Account flow never asks the
// user to pick an existing accounting ledger - that is exactly how two
// unrelated physical bank accounts could end up sharing "Main bank" and
// merging balances. `ledgerName` is an editable, auto-suggested name for a
// brand-new dedicated ledger the backend creates on save; `linkedLedgerId`
// is only ever populated via the separate "Advanced: Link existing
// accounting ledger" action (migration/import scenarios), never by default.
export const bankAccountBlank = {
  bankName: "",
  branch: "",
  accountName: "",
  accountNumber: "",
  ifsc: "",
  accountType: "savings",
  ledgerName: "",
  linkedLedgerId: "",
  isPrimaryCollectionAccount: false,
  reconciliationEnabled: true,
  active: true,
  isLinkedToBillingSettlement: false,
  // Tally-style direct opening balance (Phase 10 redesign) - entered right
  // here instead of a separate Opening Balance Migration screen.
  openingBalance: "",
  openingBalanceSide: "debit",
  openingAsOfDate: "",
};

// Client-side suggestion only, for a friendly editable default in the Add
// Bank Account form - the backend independently derives and validates the
// final ledger name and enforces uniqueness. Never suggests a generic
// placeholder like "Bank Account" or "Main bank"; those are exactly the
// shared, ambiguous names this correction eliminates.
export function suggestBankLedgerName(bankName, branch, accountNumber) {
  let bank = String(bankName || "").trim();
  if (!bank) return "";
  if (!/\bbank\b/i.test(bank)) bank = `${bank} Bank`;
  const digits = String(accountNumber || "").replace(/\D/g, "");
  const last4 = digits.length >= 4 ? digits.slice(-4) : digits;
  return last4 ? `${bank} - ${last4}` : bank;
}

// Vendor master - deliberately separate from the vendor-group Ledger it
// points at (same relationship shape as Accounting Bank Account -> Ledger).
export const vendorBlank = {
  vendorName: "",
  contactPerson: "",
  mobile: "",
  email: "",
  address: "",
  gstin: "",
  pan: "",
  paymentTerms: "",
  linkedLedgerId: NEW_LEDGER_OPTION,
  newLedgerName: "",
  active: true,
  notes: "",
  // Tally-style direct opening balance (Phase 10 redesign).
  openingBalance: "",
  openingBalanceSide: "credit",
  openingAsOfDate: "",
};

export const fundTypeLabels = {
  reserve: "Reserve Fund",
  sinking: "Sinking Fund",
  repair: "Repair Fund",
  major_repair: "Major Repair Fund",
  general: "General Fund",
  other: "Other Earmarked Fund",
};

export const fundBlank = {
  fundName: "",
  fundCode: "",
  description: "",
  linkedLedgerId: NEW_LEDGER_OPTION,
  newLedgerName: "",
  fundType: "general",
  active: true,
  startDate: "",
  notes: "",
  // Tally-style direct opening balance (Phase 10 redesign).
  openingBalance: "",
  openingBalanceSide: "credit",
  openingAsOfDate: "",
};

export const fixedDepositStatusLabels = {
  ACTIVE: "Active",
  MATURED: "Matured",
  CLOSED: "Closed",
};

export const fixedDepositBlank = {
  bankAccountId: "",
  bankName: "",
  fdNumber: "",
  instrumentName: "",
  principal: "",
  startDate: new Date().toISOString().slice(0, 10),
  maturityDate: "",
  interestRate: "",
  maturityValue: "",
  linkedLedgerId: NEW_LEDGER_OPTION,
  newLedgerName: "",
  notes: "",
  // Tally-style direct opening balance (Phase 10 redesign).
  openingBalance: "",
  openingBalanceSide: "debit",
  openingAsOfDate: "",
};

export const auditorBlank = {
  name: "",
  email: "",
  phone: "",
  firmName: "",
  membershipNo: "",
  status: "active",
  expiresAt: "",
  sendEmail: false,
};
